"use client";

import { useMemo } from "react";
import type { FieldErrors } from "react-hook-form";
import type { RfqCreateArgs } from "./types";
import type { RfqTypeConfig, RfqFormModeConfig } from "./config";
import { isCapturableEmail, isValidPhone } from "@/lib/validation/core/primitives";
import type { RfqType } from "@convex/constants/types";
import {
  isConditionRequiredForType,
  missingDimensionFields,
} from "./rfq-form-helpers";
import {
  isValidRfqPostalCode,
  isValidRfqState,
} from "./rfq-location-validation";

export interface RfqValidationInput {
  productTypeSlug?: string;
  quantity?: number;
  location?: {
    address?: {
      line1?: string;
      city?: string;
      state?: string;
      postalCode?: string;
      country?: string;
    };
    quantity?: number;
  };
  additionalLocations?: Array<{ quantity?: number }>;
  contact?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
  };
  dimensions?: {
    length?: number;
    width?: number;
    height?: number;
  };
  askingPriceCents?: number;
  condition?: string;
  attachments?: Array<{ mimeType?: string }>;
}

interface RfqValidationConfig {
  requireContact: boolean;
  requireDimensions: boolean;
  showAskingPrice: boolean;
  flow: RfqType;
  requiresInventoryDetails: boolean;
  enforcePhotoRequirement: boolean;
}

export function computeTotalQuantity(data: RfqValidationInput): number {
  const primaryQty = data.location?.quantity ?? 0;
  const additionalQty = (data.additionalLocations ?? []).reduce(
    (sum, loc) => sum + (loc.quantity ?? 0),
    0,
  );
  return primaryQty + additionalQty;
}

type RfqAddressInput = NonNullable<RfqValidationInput["location"]>["address"];

function missingAddressFields(address: RfqAddressInput): string[] {
  const missing: string[] = [];
  if (!address?.line1?.trim()) missing.push("line1");
  if (!address?.city?.trim()) missing.push("city");
  if (!isValidRfqState(address?.state, address?.country)) missing.push("state");
  if (!isValidRfqPostalCode(address?.postalCode, address?.country, address?.state)) {
    missing.push("postalCode");
  }
  return missing;
}

function missingContactFields(contact: RfqValidationInput["contact"]): string[] {
  const missing: string[] = [];
  if (!contact?.firstName?.trim()) missing.push("firstName");

  const email = contact?.email?.trim();
  if (!email || !isCapturableEmail(email)) {
    missing.push("email");
  }

  const phone = contact?.phone?.trim();
  if (!phone || !isValidPhone(phone)) {
    missing.push("phone");
  }
  return missing;
}

export function computeRfqMissingFields(
  data: RfqValidationInput,
  config: RfqValidationConfig,
  formErrors?: FieldErrors<RfqCreateArgs>,
): string[] {
  const missing: string[] = [];
  const totalQuantity = computeTotalQuantity(data);

  if (!data.productTypeSlug) missing.push("productType");
  if (totalQuantity <= 0) missing.push("quantity");

  missing.push(...missingAddressFields(data.location?.address));
  if (config.requireContact) missing.push(...missingContactFields(data.contact));
  missing.push(
    ...missingDimensionFields(data.dimensions, data.productTypeSlug, config.requireDimensions),
  );

  if (
    config.showAskingPrice &&
    (!data.askingPriceCents || data.askingPriceCents <= 0)
  ) {
    missing.push("askingPrice");
  }

  if (
    isConditionRequiredForType(data.productTypeSlug, config.flow) &&
    !data.condition
  ) {
    missing.push("condition");
  }

  if (config.requiresInventoryDetails && config.enforcePhotoRequirement) {
    const hasPhoto = data.attachments?.some((attachment) =>
      attachment.mimeType?.startsWith("image/"),
    );
    if (!hasPhoto) {
      missing.push("photos");
    }
  }

  if (formErrors?.contact?.email?.message && !missing.includes("email")) {
    missing.push("email");
  }
  if (formErrors?.contact?.phone?.message && !missing.includes("phone")) {
    missing.push("phone");
  }

  return missing;
}

export function buildValidationConfig(
  typeConfig: RfqTypeConfig,
  modeConfig: RfqFormModeConfig,
): RfqValidationConfig {
  return {
    requireContact: modeConfig.requireContact,
    requireDimensions: modeConfig.requireDimensions,
    showAskingPrice: typeConfig.showAskingPrice,
    flow: typeConfig.key,
    requiresInventoryDetails: typeConfig.requiresInventoryDetails,
    enforcePhotoRequirement: modeConfig.enforcePhotoRequirement,
  };
}

interface UseRfqFormValidationOptions {
  formState: Partial<RfqCreateArgs>;
  typeConfig: RfqTypeConfig;
  modeConfig: RfqFormModeConfig;
  formErrors?: FieldErrors<RfqCreateArgs>;
  isSubmitting: boolean;
}

interface UseRfqFormValidationReturn {
  missingFields: string[];
  canSubmit: boolean;
  totalQuantity: number;
}

export function useRfqFormValidation(
  options: UseRfqFormValidationOptions,
): UseRfqFormValidationReturn {
  const { formState, typeConfig, modeConfig, formErrors, isSubmitting } =
    options;

  const totalQuantity = useMemo(
    () => computeTotalQuantity(formState),
    [formState],
  );

  const validationConfig = useMemo(
    () => buildValidationConfig(typeConfig, modeConfig),
    [typeConfig, modeConfig],
  );

  const missingFields = useMemo(
    () => computeRfqMissingFields(formState, validationConfig, formErrors),
    [formState, validationConfig, formErrors],
  );

  const canSubmit = useMemo(
    () => missingFields.length === 0 && !isSubmitting,
    [missingFields, isSubmitting],
  );

  return {
    missingFields,
    canSubmit,
    totalQuantity,
  };
}
